import type {
  AutoConversionConfig,
  AutoDetectorConfig,
} from '@/config/types/modules/conversion';
import type {
  AutoTrackSource,
  FormAutoSource,
  LifecycleAutoSource,
  LinkAutoSource,
  PaymentAutoSource,
  SelectorAutoSource,
  UrlAutoSource,
} from '@/config/shared/auto-source';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { ConversionEventType, IConversionDetector } from '../contracts';
import { TrackerFactory } from '../tracks/TrackerFactory';
import { ConfiguredFormDetector } from '../detectors/configured/ConfiguredFormDetector';
import type { FormCapableEventType } from '../detectors/configured/ConfiguredFormDetector';
import { ConfiguredLinkDetector } from '../detectors/configured/ConfiguredLinkDetector';
import { ConfiguredSelectorDetector } from '../detectors/configured/ConfiguredSelectorDetector';
import { CardDetector } from '../detectors/card/CardDetector';
import { CheckoutDetector } from '../detectors/checkout/CheckoutDetector';
import { LifecycleAutoDetector } from '../detectors/lifecycle/LifecycleAutoDetector';
import { SelectorDetector } from '../detectors/selector/SelectorDetector';
import { SelectorResolver } from '../detectors/SelectorResolver';
import { TextDetector } from '../detectors/text/TextDetector';
import { UrlDetector } from '../detectors/url/UrlDetector';
import { isLinkSupportedEvent } from './link/LinkAutoEventMapper';
import { normalizeLinkAutoSource } from './link/normalizeLinkAutoSource';

type DetectorConfigEntry = {
  readonly configKey: keyof AutoConversionConfig['detectors'];
  readonly eventType: ConversionEventType;
};

const DETECTOR_ENTRIES: readonly DetectorConfigEntry[] = [
  { configKey: 'lead', eventType: 'LEAD' },
  { configKey: 'addToCart', eventType: 'ADD_TO_CART' },
  { configKey: 'removeFromCart', eventType: 'REMOVE_FROM_CART' },
  { configKey: 'initiateCheckout', eventType: 'INITIATE_CHECKOUT' },
  { configKey: 'purchase', eventType: 'PURCHASE' },
  { configKey: 'addPaymentInfo', eventType: 'ADD_PAYMENT_INFO' },
  { configKey: 'search', eventType: 'SEARCH' },
  { configKey: 'viewContent', eventType: 'VIEW_CONTENT' },
];

const FORM_EVENT_TYPES = new Set<ConversionEventType>([
  'LEAD',
  'ADD_TO_CART',
  'INITIATE_CHECKOUT',
]);

/**
 * Orquestra auto-detectors a partir de `detectors[event].sources[]`.
 *
 * Cada source vira um detector independente — não há mais
 * prioridade selectorForm > selector no mesmo detector.
 */
export class AutoTrackerFactory {
  static create(
    context: PixelRuntimeContext,
    factory: TrackerFactory
  ): IConversionDetector[] {
    const { auto, rules } = context.config.modules.conversion;
    const detectors: IConversionDetector[] = [];
    const logger = context.logger;

    if (!auto.enabled) {
      logger.debug('[AutoTrackerFactory] Auto detection disabled');
      return detectors;
    }

    detectors.push(...this.createSourceDetectors(context, factory, auto));

    if (rules.enabled) {
      if (rules.selectors.length > 0) {
        detectors.push(new SelectorDetector(context, factory, rules.selectors));
        logger.debug('[AutoTrackerFactory] SelectorDetector registered');
      }
      if (rules.texts.length > 0) {
        detectors.push(new TextDetector(context, factory, rules.texts));
        logger.debug('[AutoTrackerFactory] TextDetector registered');
      }
      if (rules.urlPatterns.length > 0) {
        detectors.push(new UrlDetector(context, factory, rules.urlPatterns));
        logger.debug('[AutoTrackerFactory] UrlDetector registered');
      }
      if (rules.checkout.enabled) {
        detectors.push(new CheckoutDetector(context, factory));
        logger.debug('[AutoTrackerFactory] CheckoutDetector registered');
      }
      if (rules.card.enabled) {
        detectors.push(new CardDetector(context, factory));
        logger.debug('[AutoTrackerFactory] CardDetector registered (rules)');
      }
    }

    logger.debug(
      `[AutoTrackerFactory] Created ${detectors.length} detector(s)`
    );

    return detectors;
  }

  private static createSourceDetectors(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    auto: AutoConversionConfig
  ): IConversionDetector[] {
    const detectors: IConversionDetector[] = [];
    const resolver = new SelectorResolver(context.logger);
    const logger = context.logger;
    const rulesCardActive = context.config.modules.conversion.rules.enabled
      ? context.config.modules.conversion.rules.card.enabled
      : false;

    for (const entry of DETECTOR_ENTRIES) {
      const cfg: AutoDetectorConfig = auto.detectors[entry.configKey];
      if (!cfg.enabled) {
        logger.debug(
          `[AutoTrackerFactory] Skipped disabled detector: ${entry.configKey}`
        );
        continue;
      }

      const sources = cfg.sources ?? [];
      for (const source of sources) {
        if (source.enabled === false) {
          logger.debug(
            `[AutoTrackerFactory] Skipped disabled source: ${entry.configKey}/${source.name}`
          );
          continue;
        }

        const created = this.createDetectorForSource(
          context,
          factory,
          entry.eventType,
          source,
          resolver,
          { rulesCardActive }
        );

        if (created) {
          if (
            source.type === 'payment' &&
            (source as PaymentAutoSource).cardDetection !== false
          ) {
            // Evita CardDetector duplicado se rules.card também estiver on
            // (rules já criou um; sources payment ainda podem rodar com origin própria).
            void rulesCardActive;
          }
          detectors.push(created);
          logger.debug(
            `[AutoTrackerFactory] ${created.name ?? source.type} registered for ${entry.eventType}`
          );
        }
      }
    }

    return detectors;
  }

  private static createDetectorForSource(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    eventType: ConversionEventType,
    source: AutoTrackSource,
    resolver: SelectorResolver,
    _opts: { rulesCardActive: boolean }
  ): IConversionDetector | null {
    const logger = context.logger;

    switch (source.type) {
      case 'form': {
        if (!FORM_EVENT_TYPES.has(eventType)) {
          logger.debug(
            `[AutoTrackerFactory] Skipped form source for unsupported event: ${eventType}`
          );
          return null;
        }
        return new ConfiguredFormDetector(context, factory, {
          eventType: eventType as FormCapableEventType,
          source: source as FormAutoSource,
        });
      }

      case 'selector': {
        const selectorSource = source as SelectorAutoSource;
        if (!resolver.hasEntries(selectorSource.selector)) {
          logger.debug(
            `[AutoTrackerFactory] Skipped selector source ${source.name}: empty selector`
          );
          return null;
        }
        return new ConfiguredSelectorDetector(context, factory, {
          eventType,
          source: selectorSource,
        });
      }

      case 'payment': {
        if (eventType !== 'ADD_PAYMENT_INFO') {
          logger.debug(
            `[AutoTrackerFactory] Skipped payment source for ${eventType}`
          );
          return null;
        }
        const payment = source as PaymentAutoSource;
        if (payment.cardDetection === false) {
          logger.debug(
            `[AutoTrackerFactory] Skipped payment source ${source.name}: cardDetection false`
          );
          return null;
        }
        if (payment.autoDispatch === false) {
          logger.debug(
            `[AutoTrackerFactory] Skipped payment source ${source.name}: autoDispatch false`
          );
          return null;
        }
        return new CardDetector(context, factory, { source: payment });
      }

      case 'lifecycle': {
        return new LifecycleAutoDetector(context, factory, {
          eventType,
          source: source as LifecycleAutoSource,
        });
      }

      case 'url': {
        const urlSource = source as UrlAutoSource;
        if (!urlSource.patterns?.length) {
          logger.debug(
            `[AutoTrackerFactory] Skipped url source ${source.name}: empty patterns`
          );
          return null;
        }
        return new UrlDetector(context, factory, urlSource.patterns, {
          eventType,
          sourceName: urlSource.name,
          fireOnce: urlSource.fireOnce,
        });
      }

      case 'link': {
        if (!isLinkSupportedEvent(eventType)) {
          logger.debug(
            `[AutoTrackerFactory] Skipped link source for unsupported event: ${eventType}`
          );
          return null;
        }
        let normalized;
        try {
          normalized = normalizeLinkAutoSource(source as LinkAutoSource);
        } catch (error) {
          logger.debug(
            `[AutoTrackerFactory] Skipped link source ${source.name}: normalization threw`,
            error
          );
          return null;
        }
        if (!normalized.ok) {
          logger.debug(
            `[AutoTrackerFactory] Skipped link source ${source.name}: ${normalized.reason}`
          );
          return null;
        }
        return new ConfiguredLinkDetector(context, factory, {
          eventType,
          source: normalized.source,
        });
      }

      default:
        logger.debug(
          `[AutoTrackerFactory] Unknown source type: ${(source as AutoTrackSource).type}`
        );
        return null;
    }
  }
}
